import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Checkbox, Form, Input, Popover, Select } from 'antd';
import dayjs from 'dayjs';

import useAppSelector from 'hooks/useAppSelector';

import { getMarketplaceName } from 'models/Marketplace';
import { DurationOption, durationOptions } from 'models/auction/Duration';
import { getAuctionFormErrorMsg, setAuctionFormState } from 'features/auctionFormSlice';

import ERR_MSG from 'utils/errorMessage';
import ImageField from './ImageField/ImageField';
import { productConditionOptions } from 'models/auction/Auction';
import ExplainedLabel from 'pages/common/components/ExplainedLabel';

import styles from './DescriptionForm.module.css';

const { TextArea } = Input;

function DescriptionForm() {
  const dispatch = useDispatch();
  const { loginUser } = useAppSelector((state) => state.user);
  const { marketplaces } = useAppSelector((state) => state.common);
  const { title, condition, description, duration, invitationCodeRequired, errors } = useAppSelector(
    (state) => state.auctionForm
  );

  const loginUserMarketplaceName = getMarketplaceName(marketplaces, loginUser?.marketplace_id) ?? ERR_MSG.UNKNOWN;

  const [isOpenPublicForceInvitationCodeMsg, setIsOpenPublicForceInvitationCodeMsg] = useState<boolean>(false);

  const convertExpectedEndedDateString = (duration: number) =>
    dayjs(new Date().getTime() + duration * 1000).format('MM월 DD일, A hh시 mm분 종료');

  const convertDurationOptions = (durationOptions: DurationOption[]) => {
    return durationOptions.map((option) => ({
      ...option,
      label: `${option.label} (${convertExpectedEndedDateString(option.value)})`,
    }));
  };

  const [durationOptionsWithEndedDate, setDurationOptionsWithEndedDate] = useState<DurationOption[]>(
    convertDurationOptions(durationOptions)
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDurationOptionsWithEndedDate(convertDurationOptions(durationOptions));
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <Form layout="vertical">
      <Form.Item label="마켓플레이스" required>
        <Input value={loginUserMarketplaceName} readOnly />
        <Checkbox
          className={styles['invitation-code-checkbox']}
          checked={invitationCodeRequired}
          onChange={(e) => {
            if (loginUser?.marketplace_id !== process.env.REACT_APP_PUBLIC_MARKETPLACE_ID) {
              dispatch(setAuctionFormState({ invitationCodeRequired: e.target.checked }));
            } else if (!isOpenPublicForceInvitationCodeMsg) {
              setIsOpenPublicForceInvitationCodeMsg(true);
              setTimeout(() => setIsOpenPublicForceInvitationCodeMsg(false), 2000);
            }
          }}
        >
          <Popover
            open={isOpenPublicForceInvitationCodeMsg}
            content="Public 마켓플레이스는 필수로 생성됩니다."
            placement="bottomLeft"
          >
            <ExplainedLabel
              label="초대코드 생성"
              explanation="생성 시 초대코드를 공유받은 사람만 입찰할 수 있습니다."
              placement="right"
            />
          </Popover>
        </Checkbox>
      </Form.Item>

      <Form.Item label="상품 이름" validateStatus={errors.includes('title') ? 'error' : ''} required>
        <Input
          placeholder="상품 이름을 입력해주세요"
          value={title}
          onChange={(e) => dispatch(setAuctionFormState({ title: e.target.value }))}
        />
      </Form.Item>

      <Form.Item label="상품 상태" validateStatus={errors.includes('condition') ? 'error' : ''} required>
        <Select
          placeholder="상품 상태를 선택해주세요"
          value={condition}
          options={productConditionOptions}
          onSelect={(value) => dispatch(setAuctionFormState({ condition: value }))}
        />
      </Form.Item>

      <Form.Item
        label="상품 이미지"
        validateStatus={errors.includes('images') ? 'error' : ''}
        help={errors.includes('images') ? getAuctionFormErrorMsg('images') : ''}
        required
      >
        <ImageField />
      </Form.Item>

      <Form.Item label="상품 설명" validateStatus={errors.includes('description') ? 'error' : ''} required>
        <TextArea
          placeholder="상품의 상세한 설명을 입력해주세요"
          value={description}
          onChange={(e) => dispatch(setAuctionFormState({ description: e.target.value }))}
        />
      </Form.Item>

      <Form.Item label="경매 기간" validateStatus={errors.includes('duration') ? 'error' : ''} required>
        <Select
          placeholder="경매 기간을 선택해주세요"
          value={duration}
          options={durationOptionsWithEndedDate}
          onSelect={(value) => dispatch(setAuctionFormState({ duration: value }))}
        />
      </Form.Item>

      <Form.Item
        label={<ExplainedLabel label="시작가" explanation="경매는 1,000원으로 시작합니다." placement="right" />}
        required
      >
        <Input value={1000} disabled type="number" addonBefore="₩" placeholder="시작가를 입력해주세요" />
      </Form.Item>
    </Form>
  );
}

export default DescriptionForm;
